/**
 * interest-button-dom.js
 *
 * Emphasis DOM helpers for the event interest button.
 *
 * The terms "interest" and "attendance" are used interchangeably for this button.
 *
 * The server always renders the button in the "inactive" (not interested)
 * state. This module updates the DOM to match the actual attendance state,
 * using data attributes from _interest_button.html.liquid for classes and
 * icons, and LOCALIST_SETTINGS.interest_button for labels/aria.
 *
 * States:
 *   - inactive: not interested/attending
 *   - active: interested/attending
 *   - change: same as active but only for multi-instance events; shows "Change dates"
 *     and takes priority over plain active when data-event-many-instances is set.
 *     Only used on event pages when the user is attending _any_ instance of a multi-instance event.
 *
 * Button anatomy (rendered by _interest_button.html.liquid):
 *   <a data-event-interest
 *      data-event-interest-inactive-classes="…"
 *      data-event-interest-inactive-icon="…"
 *      data-event-interest-active-classes="…"
 *      data-event-interest-active-icon="…"
 *      [data-event-is-past]
 *      [data-event-many-instances]
 *      [data-event-interest-change-classes="…" data-event-interest-change-icon="…"]
 *      data-event-action="event-going|event-not-going|event-change-attendance"
 *      data-event-id="…"
 *      [data-event-instance-id="…"]
 *      …>
 *     <i data-event-interested-icon>…</i>
 *     <span data-event-interested-label>…</span>
 *   </a>
 *
 * State is determined by data-event-action (and data-event-attending for multi when attending).
 *
 * The configuration of the button is controlled via data attributes on the button element:
 *   - data-event-interest: indicates an interest button
 *   - data-event-interest-inactive-classes: classes to apply when not interested
 *   - data-event-interest-inactive-icon: icon to apply when not interested
 *   - data-event-interest-active-classes: classes to apply when interested
 *   - data-event-interest-active-icon: icon to apply when interested
 *   - data-event-interest-change-classes: classes to apply when in the "Change Dates" state
 *   - data-event-interest-change-icon: icon to apply when in the "Change Dates" state
 *   - data-event-id: the event id
 *   - data-event-instance-id: the event instance id
 *   - data-event-is-past: whether the event is past
 *   - data-event-many-instances: whether the event has multiple future instances (a many instance button)
 *
 * Two child elements are identified by a data attribute:
 *   - data-event-interested-icon: the icon element
 *   - data-event-interested-label: the label element
 */

import classNames from "classnames";
import { fetchWithCSRF } from "../../../assets/javascripts/admin/common/helpers";
import { splitClassNames } from "../../../assets/javascripts/utils";

const getConfig = () => window.LOCALIST_SETTINGS?.interest_button ?? {};

const interpolateEventName = (string, eventName) =>
  string.replace("%{event_name}", eventName ?? "");

const findIcon = (button) => button.querySelector('[data-event-interested-icon]');

const findLabel = (button) => button.querySelector('[data-event-interested-label]');

const getAllStateClasses = (button) => {
  const { dataset } = button;
  const inactive = splitClassNames(dataset.eventInterestInactiveClasses);
  const active = splitClassNames(dataset.eventInterestActiveClasses);
  const change = splitClassNames(dataset.eventInterestChangeClasses);

  return new Set([...inactive, ...active, ...change]);
};

const applyButtonClasses = (button, classString) => {
  const stateClasses = getAllStateClasses(button);
  const keep = Array.from(button.classList).filter((c) => !stateClasses.has(c));
  button.className = classNames(keep, classString);
};

const applyIconClass = (button, iconClass, visible = true) => {
  const icon = findIcon(button);

  if (!icon) {
    return;
  }

  icon.className = classNames(iconClass);
  icon.classList.toggle("em-hidden", !visible);
};

export const isAttending = (button) =>
  button.dataset.eventAction === 'event-not-going' ||
  button.dataset.eventAttending === 'true';

export const isMultiInstance = (button) =>
  button.hasAttribute('data-event-many-instances');

// Returns all interest buttons on the page that belong to the given event instance
export const getButtonsForInstance = (instanceId) =>
  Array.from(
    document.querySelectorAll(
      `[data-event-interest][data-event-instance-id="${instanceId}"]`,
    ),
  );

// Returns all many-interest buttons on the page that belong to the given event
export const getButtonsForEvent = (eventId) =>
  Array.from(
    document.querySelectorAll(
      `[data-event-interest][data-event-many-instances][data-event-id="${eventId}"]`,
    ),
  );

const setLabels = (button, { label, ariaLabel, eventName }) => {
  const labelEl = findLabel(button);

  if (labelEl && label) {
    labelEl.textContent = interpolateEventName(label, eventName);
  }

  if (ariaLabel) {
    button.setAttribute("aria-label", interpolateEventName(ariaLabel, eventName));
  }
};

// Set a button to the "attending" visual state
//
// When data-event-many-instances is set, applies "change" state (change-classes,
// change-icon, change_dates label) and leaves href/action as event-change-attendance.
// Otherwise applies "active" state (active-classes, active-icon, unconfirm href,
// event-not-going, cancel_plans label/aria).
export const setAttending = (button) => {
  const cfg = getConfig();
  const { dataset } = button;
  const { eventName } = button.dataset;

  if (isMultiInstance(button) && !!dataset.eventInterestChangeClasses) {
    applyButtonClasses(button, dataset.eventInterestChangeClasses);
    applyIconClass(
      button,
      dataset.eventInterestChangeIcon,
      cfg.change_dates_show_icon,
    );
    button.dataset.eventAttending = 'true';

    setLabels(button, {
      label: cfg.change_dates_label,
      ariaLabel: cfg.change_dates_aria_label,
      eventName,
    });

    return;
  }

  delete button.dataset.eventAttending;

  applyButtonClasses(button, dataset.eventInterestActiveClasses);
  applyIconClass(
    button,
    dataset.eventInterestActiveIcon,
    cfg.cancel_plans_show_icon,
  );

  button.dataset.eventAction = 'event-not-going';

  const currentHref = button.getAttribute('href') ?? '';

  if (!currentHref.includes('/unconfirm')) {
    button.setAttribute('href', currentHref.replace('/confirm', '/unconfirm'));
  }

  setLabels(button, {
    label: cfg.cancel_plans_label,
    ariaLabel: cfg.cancel_plans_aria_label,
    eventName,
  });
};

// Set a button to the "not attending" (inactive) visual state
export const setNotAttending = (button) => {
  const cfg = getConfig();
  const { dataset } = button;
  const { eventName } = button.dataset;

  applyButtonClasses(button, dataset.eventInterestInactiveClasses);
  applyIconClass(
    button,
    dataset.eventInterestInactiveIcon,
    cfg.going_show_icon,
  );
  delete button.dataset.eventAttending;

  button.dataset.eventAction = isMultiInstance(button)
    ? 'event-change-attendance'
    : 'event-going';

  const currentHref = button.getAttribute('href') ?? '';

  if (!currentHref.includes('/confirm') || currentHref.includes('/unconfirm')) {
    button.setAttribute('href', currentHref.replace('/unconfirm', '/confirm'));
  }

  let label;
  let ariaLabel;

  if (button.dataset.eventIsPast !== undefined) {
    label = cfg.going_past_label;
    ariaLabel = cfg.going_past_aria_label;
  } else {
    label = cfg.going_label;
    ariaLabel = cfg.going_aria_label;
  }

  setLabels(button, {
    label,
    ariaLabel,
    eventName,
  });
};

export const collectInstanceIds = () => {
  const elements = document.querySelectorAll(
    '[data-event-interest][data-event-instance-id]',
  );
  const ids = new Set();

  for (const el of elements) {
    if (el.dataset.eventInstanceId) {
      ids.add(el.dataset.eventInstanceId);
    }
  }

  return Array.from(ids);
};

export const collectEventIds = () => {
  const elements = document.querySelectorAll(
    '[data-event-interest][data-event-many-instances][data-event-id]',
  );
  const ids = new Set();

  for (const el of elements) {
    if (el.dataset.eventId) {
      ids.add(el.dataset.eventId);
    }
  }

  return Array.from(ids);
};

// Apply instances and events maps to every matching button on the page, only mutating the DOM when the current state differs
export const applyStatusesToPage = (instances, events) => {
  const inst = instances ?? {};
  const evt = events ?? {};

  document.querySelectorAll("[data-event-interest]").forEach((button) => {
    let attending;
    if (isMultiInstance(button)) {
      const { eventId } = button.dataset;
      attending = evt[eventId]?.attending === true;
    } else {
      const { eventInstanceId } = button.dataset;
      attending = inst[eventInstanceId]?.attending === true;
    }

    if (attending && !isAttending(button)) {
      setAttending(button);
    } else if (!attending && isAttending(button)) {
      setNotAttending(button);
    }
  });
};

// Return true if the form is the "Change Dates" lightbox form
export const isGoingLightboxForm = (form) => {
  if (!form?.action) return false;
  if (!/\/event\/\d+\/confirm/.test(form.action)) return false;
  return !!form.querySelector('input[name="update_event_attendance"]');
};

export const getGoingLightboxFormInstanceIds = (form) => {
  if (!form) return [];
  const inputs = form.querySelectorAll('input[name="instance_ids[]"]');
  return Array.from(inputs)
    .map((el) => el.value?.trim())
    .filter(Boolean);
};

export const showLightboxContent = (content) => {
  window.Slzr?.Lightbox?.open?.({ padding: 0, content });
};

export const showLoginLightboxWithPostLoginAction = (postLoginAction) => {
  window.openIbox?.("#login-lightbox", { postLoginAction });
};

// Submit a confirm/unconfirm request with optimistic UI: update button and cache, then revert on failure.
const withOptimisticAttendance = async (button, attending, api, href, onSuccess) => {
  const instanceId = button.dataset.eventInstanceId;
  const eventId = button.dataset.eventId ?? null;
  const setState = attending ? setAttending : setNotAttending;
  const setOther = attending ? setNotAttending : setAttending;

  if (button.dataset.eventAttendancePending === "true") {
    return;
  }

  button.dataset.eventAttendancePending = "true";
  button.setAttribute("aria-busy", "true");

  setState(button);
  api.updateCacheEntry(instanceId, eventId, attending);

  try {
    const data = await api.postAttendance(href, instanceId);
    if (onSuccess) onSuccess(data);
    api.dispatchAttendanceChange({
      event_id: data.event_id,
      event_instance_id: data.event_instance_id,
      attending,
    });
    const cached = api.readCache();
    if (cached) {
      applyStatusesToPage(cached.instances, cached.events);
    }
  } catch {
    setOther(button);
    api.updateCacheEntry(instanceId, eventId, !attending);
  } finally {
    delete button.dataset.eventAttendancePending;
    button.removeAttribute("aria-busy");
  }
};

const handleGoingClick = async (button, event, api) => {
  event.preventDefault();
  const href = button.getAttribute("href");
  if (!href) return;
  await withOptimisticAttendance(button, true, api, href, (data) => {
    if (data.lightbox_content) {
      showLightboxContent(data.lightbox_content);
    }
  });
};

const handleNotGoingClick = async (button, event, api) => {
  event.preventDefault();
  const href = button.getAttribute("href");
  if (!href) return;
  await withOptimisticAttendance(button, false, api, href);
};

const handleChangeAttendanceClick = (button, event) => {
  event.preventDefault();

  const { eventId } = button.dataset;
  if (!eventId) return;

  const url = `/event/${eventId}/going`;
  window.openIbox?.(url, {
    onClosed: () => {
      button.focus();
    },
  });
};

const handleDocumentClick = (event, api) => {
  const button = event.target?.closest?.("[data-event-interest]");
  if (!button) return;

  if (!api.getCurrentUserId()) {
    event.preventDefault();
    const href = button.getAttribute("href");
    if (href) showLoginLightboxWithPostLoginAction(href);
    return;
  }

  const action = button.dataset.eventAction;
  if (action === "event-going") {
    handleGoingClick(button, event, api);
  } else if (action === "event-not-going") {
    handleNotGoingClick(button, event, api);
  } else if (action === "event-change-attendance") {
    handleChangeAttendanceClick(button, event);
  }
};

// Take over the choose dates modal form to handle the change of attendance states.
//
// The modal currently used on Emphasis wasn't really designed for client-side handling of the form,
// so this somewhat hacky approach is needed to keep the state in sync without a refresh.
const handleGoingLightboxSubmit = async (event, api) => {
  const form = event.target?.closest?.("form");
  if (!form || !isGoingLightboxForm(form)) return;

  delete form.dataset.remote;
  event.preventDefault();

  const formData = new FormData(form);
  formData.set("return", location.href);

  try {
    const response = await fetchWithCSRF(form.action, {
      method: "POST",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      body: formData,
    });

    if (!response.ok) {
      form.submit();
      return;
    }

    if (!response.headers.get("Content-Type")?.includes("application/json")) {
      form.submit();
      return;
    }

    const data = await response.json();
    const formInstanceIds = getGoingLightboxFormInstanceIds(form);
    const { instances, events } = api.mergeGoingLightboxResult(data, formInstanceIds);

    applyStatusesToPage(instances, events);
    window.hideIbox();

    api.dispatchAttendanceChange({
      event_id: data.event_id,
      attended_instance_ids: data.attended_instance_ids,
      attending: Array.isArray(data.attended_instance_ids) && data.attended_instance_ids.length > 0,
    });
  } catch {
    form.submit();
  }
};

// Apply any attendance changes triggered by other tabs
const handleStorageChange = (storageEvent, api) => {
  const data = api.getStorageEventData(storageEvent);
  if (!data) return;
  applyStatusesToPage(data.instances ?? {}, data.events ?? {});
};

export const attachListeners = (api) => {
  document.addEventListener("click", (event) => handleDocumentClick(event, api));
  document.addEventListener("submit", (event) => handleGoingLightboxSubmit(event, api), true);
  window.addEventListener("storage", (event) => handleStorageChange(event, api));
};
